import { Controller } from "stimulus"
import SlimSelect from "slim-select"

// Searchable single-select of the current hat's saved condition templates.
// Picking a template appends its text to the condition description field and
// resets the picker so another template can be appended.
//
// The full template text is read from the select's data-templates JSON
// attribute rather than the option elements: option labels are truncated for
// display, and SlimSelect v1 rebuilds the native options so the DOM is not a
// reliable source after init.
//
// Usage:
//   <div data-controller="condition-template-picker">
//     <select data-target="condition-template-picker.select" data-templates="[...]">
//     <input data-target="condition-template-picker.description">
//   </div>
export default class extends Controller {
  static targets = ["select", "description"]

  connect() {
    // The picker select is only rendered when the current hat has templates,
    // but the controller is attached to the form unconditionally.
    if (!this.hasSelectTarget) return

    this.templates = JSON.parse(this.selectTarget.getAttribute("data-templates") || "[]")

    // A Turbo cache snapshot may already contain SlimSelect markup from a
    // previous visit (the snapshot is taken before disconnect runs). Remove
    // any stray widgets so re-initializing doesn't render the select twice.
    this.removeStrayWidgets()

    this.slimSelect = new SlimSelect({
      select: this.selectTarget,
      addToBody: true,
      onChange: () => this.templatePicked()
    })

    // Tear down before Turbo snapshots the page so the cached DOM holds the
    // pristine native select instead of the generated SlimSelect widget.
    this.beforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.teardown()
  }

  templatePicked() {
    const id = parseInt(this.selectTarget.value, 10)
    if (Number.isNaN(id)) return

    const template = this.templates.find((entry) => entry.id === id)
    if (template && this.hasDescriptionTarget) {
      this.appendDescription(template.description)
    }

    // Reset so the same or another template can be appended again. Guarded by
    // the NaN check above: this re-triggers onChange with a blank value.
    this.slimSelect.set("")
  }

  appendDescription(text) {
    const field = this.descriptionTarget
    const current = field.value.trim()
    field.value = current.length ? `${current} ${text}` : text
    field.dispatchEvent(new Event("change", { bubbles: true }))
  }

  teardown() {
    if (this.slimSelect) {
      try {
        this.slimSelect.destroy()
      } catch (error) {
        // SlimSelect DOM may already be gone on Turbo teardown.
      }
      this.slimSelect = null
    }
    this.removeStrayWidgets()
  }

  removeStrayWidgets() {
    if (!this.hasSelectTarget) return

    const parent = this.selectTarget.parentNode
    if (!parent) return

    parent.querySelectorAll(".ss-main").forEach((widget) => widget.remove())
    this.selectTarget.style.display = ""
  }
}
